import { ArrowDown, ArrowUp, GripVertical, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { cn, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@nexus/ui"
import type { MyBrandKpiCatalogEntry } from "../lib/my-brand-kpi-catalog"
import type { DrilldownSortDirection } from "../stores/drilldownFilters.store"

interface MyBrandKpiPillProps {
  id: string
  entry: MyBrandKpiCatalogEntry
  catalog: MyBrandKpiCatalogEntry[]
  isActiveSort: boolean
  sortDirection: DrilldownSortDirection
  canRemove: boolean
  /** All currently-selected ids (for excluding from the replace dropdown). */
  selectedIds: string[]
  onToggleSort: (id: string) => void
  onRemove: (id: string) => void
  onReplace: (oldId: string, newId: string) => void
}

/** One My Brand KPI pill: drag handle, color dot + name (click opens a replace
 *  dropdown), sort-direction arrow (highlighted in the KPI's own color when it's the
 *  active sort), and a remove button. Modeled on overview/components/OverviewChart's
 *  MetricPill, with drag-reorder added and sort state shared (not per-pill). */
export function MyBrandKpiPill({
  id,
  entry,
  catalog,
  isActiveSort,
  sortDirection,
  canRemove,
  selectedIds,
  onToggleSort,
  onRemove,
  onReplace,
}: MyBrandKpiPillProps) {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const SortIcon = sortDirection === "desc" ? ArrowDown : ArrowUp
  const sortDirectionLabel = t(
    sortDirection === "desc" ? "drilldown.myBrandKpi.sortDescending" : "drilldown.myBrandKpi.sortAscending",
    sortDirection === "desc" ? "descending" : "ascending",
  )
  const replaceOptions = catalog.filter((e) => e.id === id || !selectedIds.includes(e.id))

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        borderColor: isActiveSort ? `color-mix(in srgb, ${entry.colorVar} 40%, transparent)` : undefined,
      }}
      data-testid={`my-brand-kpi-pill-${id}`}
      className={cn(
        "flex h-[37.5px] items-center overflow-hidden rounded-[10px] border border-border bg-background",
        isDragging && "z-50 shadow-md",
      )}
    >
      <button
        type="button"
        aria-label={t("drilldown.myBrandKpi.reorder", "Reorder {{label}}", { label: entry.label })}
        className="flex h-full cursor-grab touch-none items-center pl-2 pr-1 text-muted-foreground/50 hover:text-muted-foreground active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-3" />
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t("drilldown.myBrandKpi.changeKpi", "Change KPI (currently {{label}})", { label: entry.label })}
            className="flex h-full items-center gap-2 pr-3 text-[13px] font-medium tracking-[-0.08px] text-foreground hover:bg-muted"
          >
            <span className="size-4 shrink-0 rounded-[6px]" style={{ background: entry.colorVar }} aria-hidden />
            {entry.label}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-[320px] w-[220px] overflow-y-auto">
          {replaceOptions.map((option) => (
            <DropdownMenuItem
              key={option.id}
              onSelect={() => onReplace(id, option.id)}
              className={cn("gap-2", option.id === id && "font-medium text-primary")}
            >
              <span className="size-3 shrink-0 rounded-[4px]" style={{ background: option.colorVar }} aria-hidden />
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <button
        type="button"
        aria-label={t("drilldown.myBrandKpi.sortBy", "Sort by {{label}}, currently {{direction}}", {
          label: entry.label,
          direction: sortDirectionLabel,
        })}
        data-testid={`my-brand-kpi-pill-${id}-sort`}
        onClick={() => onToggleSort(id)}
        style={isActiveSort ? { color: entry.colorVar } : undefined}
        className={cn(
          "flex h-full items-center border-l border-border px-2 hover:bg-muted",
          !isActiveSort && "text-muted-foreground",
        )}
      >
        <SortIcon className="size-3.5" />
      </button>

      <button
        type="button"
        aria-label={t("drilldown.myBrandKpi.remove", "Remove {{label}}", { label: entry.label })}
        data-testid={`my-brand-kpi-pill-${id}-remove`}
        disabled={!canRemove}
        onClick={() => onRemove(id)}
        className={cn(
          "flex h-full items-center border-l border-border px-2 text-muted-foreground hover:bg-muted hover:text-foreground",
          !canRemove && "cursor-not-allowed opacity-40 hover:bg-transparent hover:text-muted-foreground",
        )}
      >
        <X className="size-3.5" />
      </button>
    </div>
  )
}
